"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { LOCATIONS, type LocationData } from "@/lib/regional-multipliers";
import { PRICING_DATA, type TradePricing } from "@/lib/pricing-data";
import { ShareButton } from "@/app/components/ShareButton";

const sortedLocations = [...LOCATIONS].sort((a, b) =>
  a.name.localeCompare(b.name)
);

const sortedTrades = [...PRICING_DATA].sort((a, b) =>
  a.tradeName.localeCompare(b.tradeName)
);

function getCityPrice(
  labourLow: number,
  labourHigh: number,
  materialsLow: number,
  materialsHigh: number,
  multiplier: number
): { low: number; high: number; avg: number } {
  const low = Math.round((labourLow + materialsLow) * multiplier);
  const high = Math.round((labourHigh + materialsHigh) * multiplier);
  return { low, high, avg: Math.round((low + high) / 2) };
}

function formatGBP(n: number): string {
  return `£${n.toLocaleString("en-GB")}`;
}

interface CompareClientProps {
  initialCity1?: string;
  initialCity2?: string;
  initialTrade?: string;
}

export function CompareClient({
  initialCity1,
  initialCity2,
  initialTrade,
}: CompareClientProps) {
  const [city1Slug, setCity1Slug] = useState(initialCity1 ?? "");
  const [city2Slug, setCity2Slug] = useState(initialCity2 ?? "");
  const [tradeSlug, setTradeSlug] = useState(initialTrade ?? "");
  const [showResults, setShowResults] = useState(
    !!(initialCity1 && initialCity2 && initialTrade)
  );

  const city1 = useMemo(
    () => sortedLocations.find((l) => l.slug === city1Slug),
    [city1Slug]
  );
  const city2 = useMemo(
    () => sortedLocations.find((l) => l.slug === city2Slug),
    [city2Slug]
  );
  const trade = useMemo(
    () => sortedTrades.find((t) => t.tradeSlug === tradeSlug),
    [tradeSlug]
  );

  const comparison = useMemo(() => {
    if (!city1 || !city2 || !trade) return null;
    return buildComparison(city1, city2, trade);
  }, [city1, city2, trade]);

  function handleCompare() {
    if (city1Slug && city2Slug && tradeSlug) {
      setShowResults(true);
    }
  }

  function handleReset() {
    setShowResults(false);
    setCity1Slug("");
    setCity2Slug("");
    setTradeSlug("");
  }

  const canCompare = city1Slug && city2Slug && tradeSlug && city1Slug !== city2Slug;
  const shareUrl = showResults && city1 && city2 && trade
    ? `/compare?cities=${city1.slug},${city2.slug}&trade=${trade.tradeSlug}`
    : "";

  return (
    <div className="max-w-5xl mx-auto px-5">
      {/* Selection form */}
      {!showResults && (
        <div className="bg-white rounded-md border border-stone p-6 md:p-8">
          <h2 className="font-serif text-xl font-semibold text-charcoal mb-6">
            Select two cities and a trade to compare
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* City 1 */}
            <div>
              <label
                htmlFor="city1"
                className="block text-sm font-medium text-text-body mb-1.5"
              >
                First city
              </label>
              <select
                id="city1"
                value={city1Slug}
                onChange={(e) => setCity1Slug(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone rounded-sm text-sm text-text-body bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest"
              >
                <option value="">Select a city</option>
                {sortedLocations.map((loc) => (
                  <option key={loc.slug} value={loc.slug} disabled={loc.slug === city2Slug}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            {/* City 2 */}
            <div>
              <label
                htmlFor="city2"
                className="block text-sm font-medium text-text-body mb-1.5"
              >
                Second city
              </label>
              <select
                id="city2"
                value={city2Slug}
                onChange={(e) => setCity2Slug(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone rounded-sm text-sm text-text-body bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest"
              >
                <option value="">Select a city</option>
                {sortedLocations.map((loc) => (
                  <option key={loc.slug} value={loc.slug} disabled={loc.slug === city1Slug}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Trade */}
            <div>
              <label
                htmlFor="trade"
                className="block text-sm font-medium text-text-body mb-1.5"
              >
                Trade
              </label>
              <select
                id="trade"
                value={tradeSlug}
                onChange={(e) => setTradeSlug(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone rounded-sm text-sm text-text-body bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest"
              >
                <option value="">Select a trade</option>
                {sortedTrades.map((t) => (
                  <option key={t.tradeSlug} value={t.tradeSlug}>
                    {t.tradeName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {city1Slug === city2Slug && city1Slug !== "" && (
            <p className="text-sm text-copper mb-4">
              Please select two different cities to compare.
            </p>
          )}

          <button
            type="button"
            onClick={handleCompare}
            disabled={!canCompare}
            className="px-6 py-3 bg-forest text-white font-semibold rounded-sm text-sm transition-colors hover:bg-forest-light disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Compare Prices
          </button>
        </div>
      )}

      {/* Results */}
      {showResults && comparison && city1 && city2 && trade && (
        <div className="space-y-8">
          {/* Summary */}
          <div className="bg-white rounded-md border border-stone p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-charcoal mb-2">
                  {trade.tradeName} costs: {city1.name} vs {city2.name}
                </h2>
                <p className="text-text-body text-base">
                  {comparison.summary}
                </p>
              </div>
              {shareUrl && (
                <ShareButton
                  url={shareUrl}
                  title={`${trade.tradeName} costs: ${city1.name} vs ${city2.name}`}
                  text={comparison.summary}
                />
              )}
            </div>

            {/* Overall percentage difference */}
            <div className="bg-sage-pale rounded-sm p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center shrink-0">
                <svg
                  className="w-5 h-5 text-forest"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-charcoal">
                  Overall: {comparison.cheaperCity} is{" "}
                  {comparison.overallPercentDiff}% cheaper on average
                </p>
                <p className="text-xs text-text-muted mt-0.5">
                  Based on average prices across {comparison.jobComparisons.length} job types
                </p>
              </div>
            </div>
          </div>

          {/* Job-by-job comparison */}
          <div className="bg-white rounded-md border border-stone p-6 md:p-8">
            <h3 className="font-serif text-lg font-semibold text-charcoal mb-6">
              Price comparison by job type
            </h3>

            <div className="space-y-6">
              {comparison.jobComparisons.map((job) => (
                <div key={job.slug} className="pb-6 border-b border-stone last:border-b-0 last:pb-0">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                    <h4 className="font-semibold text-charcoal text-sm">
                      {job.name}
                    </h4>
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full w-fit ${
                        job.percentDiff === 0
                          ? "bg-stone/50 text-text-muted"
                          : "bg-copper/10 text-copper"
                      }`}
                    >
                      {job.percentDiff === 0
                        ? "Same price"
                        : `${job.cheaperCity} is ${job.percentDiff}% cheaper`}
                    </span>
                  </div>

                  {/* Bar chart */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-text-muted w-28 md:w-36 shrink-0 truncate">
                        {city1.name}
                      </span>
                      <div className="flex-1 h-7 bg-warm-bg rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-forest/80 rounded-sm flex items-center justify-end px-2"
                          style={{
                            width: `${(job.city1Avg / comparison.maxAvg) * 100}%`,
                          }}
                        >
                          <span className="text-xs font-medium text-white whitespace-nowrap">
                            {formatGBP(job.city1Low)} - {formatGBP(job.city1High)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-text-muted w-28 md:w-36 shrink-0 truncate">
                        {city2.name}
                      </span>
                      <div className="flex-1 h-7 bg-warm-bg rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-copper/80 rounded-sm flex items-center justify-end px-2"
                          style={{
                            width: `${(job.city2Avg / comparison.maxAvg) * 100}%`,
                          }}
                        >
                          <span className="text-xs font-medium text-white whitespace-nowrap">
                            {formatGBP(job.city2Low)} - {formatGBP(job.city2High)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Find tradespeople CTAs */}
          <div className="bg-cream rounded-md p-6 md:p-8">
            <h3 className="font-serif text-lg font-semibold text-charcoal mb-2">
              Find {trade.tradeName.toLowerCase()}s in these areas
            </h3>
            <p className="text-text-muted text-sm mb-5">
              Tradesfolk is 100% free for homeowners. Browse profiles, read
              reviews, and get quotes at no cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href={`/browse?trade=${trade.tradeSlug}&name=${encodeURIComponent(city1.name)}`}
                className="inline-flex items-center justify-center px-5 py-3 bg-forest text-white font-semibold rounded-sm text-sm no-underline transition-colors hover:bg-forest-light"
              >
                {trade.tradeName}s in {city1.name}
              </Link>
              <Link
                href={`/browse?trade=${trade.tradeSlug}&name=${encodeURIComponent(city2.name)}`}
                className="inline-flex items-center justify-center px-5 py-3 border border-forest text-forest font-semibold rounded-sm text-sm no-underline transition-colors hover:bg-forest/5"
              >
                {trade.tradeName}s in {city2.name}
              </Link>
            </div>
          </div>

          {/* Related tools */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              href={`/prices/${trade.tradeSlug}`}
              className="p-4 bg-white rounded-md border border-stone text-center no-underline hover:border-sage-light transition-colors group"
            >
              <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">View {trade.tradeName} Prices</p>
              <p className="text-xs text-text-muted mt-1">Full price breakdown</p>
            </Link>
            <Link
              href={`/leaderboards/${trade.tradeSlug}`}
              className="p-4 bg-white rounded-md border border-stone text-center no-underline hover:border-sage-light transition-colors group"
            >
              <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">{trade.tradeName} Leaderboard</p>
              <p className="text-xs text-text-muted mt-1">All 200+ cities ranked</p>
            </Link>
            <Link
              href="/cost-estimator"
              className="p-4 bg-white rounded-md border border-stone text-center no-underline hover:border-sage-light transition-colors group"
            >
              <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">Cost Estimator</p>
              <p className="text-xs text-text-muted mt-1">Get a personalised quote</p>
            </Link>
          </div>

          {/* Compare another */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 bg-forest text-white font-semibold rounded-sm text-sm transition-colors hover:bg-forest-light"
            >
              Compare another trade
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface JobComparison {
  slug: string;
  name: string;
  city1Low: number;
  city1High: number;
  city1Avg: number;
  city2Low: number;
  city2High: number;
  city2Avg: number;
  percentDiff: number;
  cheaperCity: string;
}

interface ComparisonResult {
  jobComparisons: JobComparison[];
  overallPercentDiff: number;
  cheaperCity: string;
  summary: string;
  maxAvg: number;
}

function buildComparison(
  city1: LocationData,
  city2: LocationData,
  trade: TradePricing
): ComparisonResult {
  const jobComparisons: JobComparison[] = trade.jobTypes.map((job) => {
    const c1 = getCityPrice(
      job.labourLow,
      job.labourHigh,
      job.materialsLow,
      job.materialsHigh,
      city1.multiplier
    );
    const c2 = getCityPrice(
      job.labourLow,
      job.labourHigh,
      job.materialsLow,
      job.materialsHigh,
      city2.multiplier
    );
    const diff =
      c1.avg === c2.avg
        ? 0
        : Math.round(
            (Math.abs(c1.avg - c2.avg) / Math.max(c1.avg, c2.avg)) * 100
          );
    const cheaperCity = c1.avg <= c2.avg ? city1.name : city2.name;
    return {
      slug: job.slug,
      name: job.name,
      city1Low: c1.low,
      city1High: c1.high,
      city1Avg: c1.avg,
      city2Low: c2.low,
      city2High: c2.high,
      city2Avg: c2.avg,
      percentDiff: diff,
      cheaperCity,
    };
  });

  const totalCity1 = jobComparisons.reduce((s, j) => s + j.city1Avg, 0);
  const totalCity2 = jobComparisons.reduce((s, j) => s + j.city2Avg, 0);
  const overallPercentDiff =
    totalCity1 === totalCity2
      ? 0
      : Math.round(
          (Math.abs(totalCity1 - totalCity2) /
            Math.max(totalCity1, totalCity2)) *
            100
        );
  const cheaperCity = totalCity1 <= totalCity2 ? city1.name : city2.name;

  const exampleJob = jobComparisons[0];
  const summary =
    overallPercentDiff === 0
      ? `${trade.tradeName} work costs roughly the same in ${city1.name} and ${city2.name}.`
      : `${exampleJob.name} in ${cheaperCity} is ${overallPercentDiff}% cheaper than in ${cheaperCity === city1.name ? city2.name : city1.name} on average.`;

  const maxAvg = Math.max(...jobComparisons.map((j) => Math.max(j.city1Avg, j.city2Avg)));

  return { jobComparisons, overallPercentDiff, cheaperCity, summary, maxAvg };
}
